import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-24 px-4 md:px-8">
          <div className="text-center py-20">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">
              文章未找到
            </h1>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 transition-colors"
            >
              返回首页
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image,
    "datePublished": article.date,
    "author": {
      "@type": "Person",
      "name": article.author,
      "image": article.authorAvatar
    },
    "publisher": {
      "@type": "Organization",
      "name": "洞察日报",
      "logo": {
        "@type": "ImageObject",
        "url": "http://dlqlbdnn.cn/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `http://dlqlbdnn.cn/article/${article.id}`
    },
    "articleSection": article.categoryName,
    "keywords": article.tags.join(", ")
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "http://dlqlbdnn.cn/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": article.categoryName,
        "item": `http://dlqlbdnn.cn/category/${article.category}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.title,
        "item": `http://dlqlbdnn.cn/article/${article.id}`
      }
    ]
  };

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="max-w-4xl mx-auto px-4 md:px-8 pb-12 md:pb-20">
          <nav className="text-sm text-foreground-500 mb-6">
            <Link to="/" className="hover:text-foreground-700 transition-colors">
              首页
            </Link>
            <span className="mx-2">/</span>
            <Link
              to={`/category/${article.category}`}
              className="hover:text-foreground-700 transition-colors"
            >
              {article.categoryName}
            </Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-950 line-clamp-1 inline-block max-w-xs align-bottom">
              {article.title}
            </span>
          </nav>

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-primary-100 text-primary-700 text-xs font-medium px-3 py-1 rounded-full">
                {article.categoryName}
              </span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-background-100 text-foreground-500 text-xs px-3 py-1 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 mt-6">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="font-medium text-foreground-950 text-sm">
                  {article.author}
                </p>
                <div className="flex items-center gap-2 text-xs text-foreground-500">
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.readTime}阅读</span>
                  <span>·</span>
                  <span>{article.views.toLocaleString()} 阅读</span>
                </div>
              </div>
            </div>
          </header>

          <div className="rounded-2xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-[300px] md:h-[500px] object-cover"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            {article.content.split("\n\n").map((paragraph, idx) => {
              if (paragraph.startsWith("## ")) {
                return (
                  <h2
                    key={idx}
                    className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4"
                  >
                    {paragraph.replace("## ", "")}
                  </h2>
                );
              }
              if (paragraph.startsWith("一、") || paragraph.startsWith("二、") || paragraph.startsWith("三、") || paragraph.startsWith("四、") || paragraph.startsWith("五、") || paragraph.startsWith("六、") || paragraph.startsWith("七、")) {
                return (
                  <h3
                    key={idx}
                    className="font-heading text-xl font-bold text-foreground-950 mt-8 mb-3"
                  >
                    {paragraph}
                  </h3>
                );
              }
              return (
                <p
                  key={idx}
                  className="text-foreground-700 leading-relaxed mb-4 text-base md:text-lg"
                >
                  {paragraph}
                </p>
              );
            })}
          </div>

          <div className="border-t border-background-200/70 mt-12 pt-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium text-foreground-950">
                    {article.author}
                  </p>
                  <p className="text-sm text-foreground-500">专栏作者</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button className="flex items-center gap-2 px-4 py-2 border border-background-200/70 rounded-full text-sm text-foreground-600 hover:bg-background-100 transition-colors">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-heart-line" />
                  </span>
                  {article.likes}
                </button>
                <button className="flex items-center gap-2 px-4 py-2 border border-background-200/70 rounded-full text-sm text-foreground-600 hover:bg-background-100 transition-colors">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-share-line" />
                  </span>
                  分享
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="px-4 md:px-8 pb-12 md:pb-20 bg-background-100 py-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  className="group block bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="relative h-44 md:h-52 overflow-hidden">
                    <img
                      src={related.image}
                      alt={related.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-heading text-base font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                      {related.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-3">
                      <span className="text-xs text-foreground-500">
                        {related.date}
                      </span>
                      <span className="text-foreground-300">·</span>
                      <span className="text-xs text-foreground-500">
                        {related.readTime}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}